import { useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import {
  editorError,
  isAccessError,
  proposalError,
} from "@/payroll/editor/state";
import { CellInspector } from "./cell-inspector";
import { cellAddress, MARK_LABEL } from "./cell-marks";
import { ChangeSummary } from "./change-summary";
import { ComparisonGridSkeleton } from "./comparison-grid-skeleton";
import { useComparison, useComparisonSheet } from "./comparison-query";
import { ComparisonSides } from "./comparison-sides";
import { ComparisonSkeleton } from "./comparison-skeleton";
import { ComparisonToolbar } from "./comparison-toolbar";
import { LENS, type Lens } from "./lens";
import { notReadyNote } from "./not-ready-note";
import { SheetStrip } from "./sheet-strip";
import { StatementsView } from "./statements-view";
import type { EnrichedComparisonSheet } from "./types";
import { useComparisonSearch } from "./use-comparison-search";
import { useMirroredScroll } from "./use-mirrored-scroll";

// The toolbar sits above a dense grid, so its controls are sized to it rather
// than to the page's default button.
const CONTROL = "h-7 rounded-md px-2.5 text-xs font-medium";
// Bare icons, no chrome: a toolbar over a spreadsheet does not need a box
// drawn around every action.

// Sheets that exist on only one side say so; everything else uses the dot.

const EMPTY_SHEET: Pick<
  EnrichedComparisonSheet,
  "marks" | "sides" | "changes" | "counts" | "rows" | "cols"
> = {
  marks: new Map(),
  sides: { current: new Map(), proposed: new Map() },
  changes: [],
  counts: { changed: 0, added: 0, removed: 0, format: 0 },
  rows: 0,
  cols: 0,
};

/**
 * The customer's comparator: their published payroll and their proposed payroll
 * run over the same real pay period, side by side, sheet by sheet.
 */
export const ComparisonView = ({
  businessId,
  workspaceId,
  editable,
  payMayMove,
  active,
  periodStart,
  onPeriodChange,
}: {
  businessId: string;
  workspaceId: string;
  editable: boolean;
  /** False when nothing in this proposal could change a number. */
  payMayMove: boolean;
  /** The period this pane is about, held by the pane so the publish gate and
   * this view can never be looking at two different ones. */
  periodStart: string | undefined;
  onPeriodChange: (start: string | undefined) => void;
  /** A request is running, so a new comparison may start on its own. */
  active: boolean;
}) => {
  const [sheetIndex, setSheetIndex] = useState<number | null>(null);
  // A payroll has twenty-odd sheets and a rule change touches a handful, so the
  // ones that moved are the default view.
  const [changedOnly, setChangedOnly] = useState(true);
  // Two readings of the same run: the numbers, and the documents the numbers
  // turn into. Owners think in statements; the grid is the proof underneath.
  const [lens, setLens] = useState<Lens>(LENS.numbers);
  const [inspecting, setInspecting] = useState<string | null>(null);
  const { state, run, path, key } = useComparison(
    businessId,
    workspaceId,
    periodStart,
    true,
    active,
  );
  // Nothing auto-starts here any more. The run begins when the edit is saved,
  // and the Pay tab marks itself once it is ready, so arriving at this view is
  // never a request to start work — it is a request to read work that is done
  // or under way. When neither is true the view offers its own Run button,
  // which is the honest state rather than a spinner nobody asked for.

  const sheets = state.data?.sheets ?? [];
  // A renamed sheet counts as worth showing even though renaming moves no
  // number. The API deliberately leaves a rename out of the changed count, so a
  // script that adopts a new naming convention does not report a hundred
  // changed sheets, but the one sheet you just renamed should not then be
  // hidden behind "only the ones that changed".
  const changedSheets = state.data?.changedSheets ?? [];
  const listed = changedOnly && changedSheets.length ? changedSheets : sheets;
  const activeIndex = listed.some((entry) => entry.index === sheetIndex)
    ? sheetIndex
    : (listed[0]?.index ?? null);
  const sheet = useComparisonSheet(path, key, periodStart, activeIndex);
  const { marks, sides, changes, counts } = sheet.data ?? EMPTY_SHEET;
  const { currentRef, proposedRef } = useMirroredScroll(
    sheet.data,
    lens !== LENS.statements && !!state.data?.ready,
  );

  const pane = useRef<HTMLElement>(null);
  const search = useComparisonSearch({ sides, pane, onSelect: setInspecting });

  const describe = (row: number, col: number) => {
    const key = `${row}:${col}`;
    const mark = marks.get(key);
    const before = sides.current.get(key);
    const after = sides.proposed.get(key);
    if (!mark) return after ?? before;
    if (mark === "changed")
      return `${MARK_LABEL[mark]}. Today: ${before || "empty"}. Your proposal: ${after || "empty"}.`;
    if (mark === "format")
      return `${MARK_LABEL[mark]}. The value is still ${after || before || "empty"}.`;
    return `${MARK_LABEL[mark]}: ${after || before || "empty"}.`;
  };

  const address = inspecting
    ? cellAddress(
        Number(inspecting.split(":")[0]),
        Number(inspecting.split(":")[1]),
      )
    : null;

  const goToChange = (step: number) => {
    if (!changes.length) return;
    const current = changes.findIndex(
      ([row, col]) => inspecting === `${row}:${col}`,
    );
    const next =
      current < 0
        ? step > 0
          ? 0
          : changes.length - 1
        : (current + step + changes.length) % changes.length;
    const [row, col] = changes[next];
    setInspecting(`${row}:${col}`);
    for (const node of [currentRef.current, proposedRef.current]) {
      const cell = node?.querySelector(`[data-coord="${row}:${col}"]`);
      cell?.scrollIntoView({ block: "center", inline: "center" });
    }
  };

  if (state.isPending) return <ComparisonSkeleton />;
  // A failed poll is not a lost comparison. This polls every five seconds, and
  // one blip used to replace minutes of finished work with an error page. The
  // result stays on screen and a line below the toolbar says the refresh
  // failed. Access being refused is different: that data is not theirs to see
  // any more, so it goes.
  if (state.isError && (!state.data || isAccessError(state.error)))
    return (
      <Stack
        gap={0}
        align="stretch"
        direction="column"
        className="flex flex-col gap-2 p-4"
        role="alert"
      >
        {/* The API says why a comparison cannot run, in the owner's terms.
            A generic sentence about the request is not the same thing. */}
        <p>{proposalError(state.error)}</p>
        <Button
          size="xs"
          variant="outline"
          className={CONTROL}
          onClick={() => void state.refetch()}
        >
          Try again
        </Button>
      </Stack>
    );

  const data = state.data;
  // Whether the sheet-level chrome has anything to describe yet.
  const browsing = lens !== LENS.statements && !!data?.ready;
  return (
    <section
      ref={pane}
      className="flex min-h-0 flex-col"
      aria-label="Payroll comparison"
    >
      {/* One grey block, one line.

          The period, the sheet you are on and what moved are three rows of the
          same thing: the editor's controls over the comparison. Each used to
          draw its own bottom border, which ruled the grey into three stripes
          and left the artifact's own edge no stronger than the lines above it.
          The strong line now sits once, at the foot of the block, where the
          chrome stops and the sheet begins. */}
      {/* The PayPack lens draws its own foot, so the block does not draw one
          too: two strong lines with a grey band between them is the striping
          this grouping removed. */}
      <div
        className={cn("shrink-0", {
          "border-b-2 border-line": lens !== LENS.statements,
        })}
      >
        <ComparisonToolbar
          data={data}
          state={state}
          run={run}
          periodStart={periodStart}
          setPeriodStart={(start) => {
            onPeriodChange(start);
            setSheetIndex(null);
            setInspecting(null);
          }}
          pickerPeriods={data?.pickerPeriods ?? []}
          payMayMove={payMayMove}
          editable={editable}
          lens={lens}
          setLens={setLens}
        />
        {state.isError && (
          <Stack
            gap={0}
            align="stretch"
            direction="row"
            role="status"
            className="flex items-center gap-2 px-4 py-1 text-xs text-subtle"
          >
            <span>Could not check for an update. Showing the last result.</span>
            <Button
              size="xs"
              variant="ghost"
              className="h-auto p-0"
              onClick={() => void state.refetch()}
            >
              Try again
            </Button>
          </Stack>
        )}
        {browsing && (
          <>
            <SheetStrip
              listed={listed}
              all={sheets}
              activeIndex={activeIndex}
              onSelect={(index: number) => {
                setSheetIndex(index);
                setInspecting(null);
                // Choosing a sheet shows that sheet. The switcher offers every
                // sheet while the strip may be filtered to the ones that
                // moved, so picking an unchanged sheet set an index the filter
                // then threw away and the click did nothing at all.
                if (!changedSheets.some((entry) => entry.index === index))
                  setChangedOnly(false);
              }}
            />
            <ChangeSummary
              search={search}
              changedSheets={changedSheets}
              sheets={sheets}
              changes={changes}
              counts={counts}
              goToChange={goToChange}
              changedOnly={changedOnly}
              onChangedOnlyChange={(value) => {
                setChangedOnly(value);
                setSheetIndex(null);
              }}
            />
          </>
        )}
      </div>

      {lens === LENS.statements ? (
        <StatementsView
          key={periodStart ?? data?.period.start_date}
          path={path}
          queryKey={key}
          periodStart={periodStart ?? data?.period.start_date}
          enabled={!!data?.ready}
          note={notReadyNote(
            { running: data?.running, editable, payMayMove },
            "Run the comparison to see each technician's statement with today's rules beside your proposal.",
          )}
        />
      ) : !data?.ready ? (
        <Empty
          className={emptyStateVariants({
            layout: "inline",
            className: "px-4",
          })}
        >
          <EmptyHeader>
            <EmptyDescription>
              {notReadyNote(
                { running: data?.running, editable, payMayMove },
                "Run the comparison to see this pay period calculated with today's rules beside your proposed rules.",
              )}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          {/* The same grid the whole-pane skeleton draws, so switching sheets
              redraws nothing above it. */}
          {sheet.isPending && <ComparisonGridSkeleton />}
          {sheet.isError && (
            <p className="p-4" role="alert">
              {editorError(sheet.error)}
            </p>
          )}
          {!!sheet.data && (
            <ComparisonSides
              sheet={sheet.data}
              address={address}
              inspecting={inspecting}
              onInspect={setInspecting}
              describe={describe}
              currentRef={currentRef}
              proposedRef={proposedRef}
            />
          )}
          {!!sheet.data && (
            <CellInspector
              coord={inspecting}
              address={address}
              labels={inspecting ? sheet.data.labels?.[inspecting] : undefined}
              before={inspecting ? sides.current.get(inspecting) : undefined}
              after={inspecting ? sides.proposed.get(inspecting) : undefined}
              mark={inspecting ? marks.get(inspecting) : undefined}
            />
          )}
        </>
      )}
    </section>
  );
};
